/**
 * クラス名：担当者パラメータ
 * 説明：担当者情報一覧画面
 * 作成者：ケイ
 * 作成日：2023/05/01
 * バージョン：1.0
 */
import { Table, Button, Row, Input, Col, Modal, Select, Space, Image, Typography } from 'antd';
import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import 'split-pane-react/esm/themes/default.css';
import { StatusCodes } from 'http-status-codes';

import {
  confirmUpdateContent,
  confirmDiscardContent,
  confirmModalTitle,
  confirmDeleteContent,
  confirmDiscardAndDeleteContent,
  commonFooter,
  error,
  authorizeError,
  commonDeleteFooter,
  commonResponsiveConfirmModal,
  commonResponsiveModal,
} from '../common/CommonModal';
import { updateAccessToken, getAccessToken, ResizableTitle } from '../common/Common';
import { ErrorMessage } from '../common/Message';
import { pageSizes } from '../common/Constant';
import { SearchOutlined } from '@ant-design/icons';
import { getUserMasterInfo } from '../common/CommonAPI';
import { CompanyRole, UserRoleIntToString } from '../common/enums';
import editIcon from '../../assets/images/edit_info_usr.png';
import deleteIcon from '../../assets/images/delete_usr.png';
import reload from '../../assets/images/reload.png';

const UserMasterList = forwardRef((props, ref) => {
  const [userMasterData, setUserMasterData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(0);
  const [selectedCompanyMasterId, setSelectedCompanyMasterId] = useState(0);
  const [dataState, setDataState] = useState(1);
  const [keyword, setKeyWord] = useState('');
  const [isBulkEdit, setIsBulkEdit] = useState(false);
  const [isCreateNew, setIsCreateNew] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [updateConfirm, setUpdateConfirm] = useState(false);
  const [tmpFilteredData, setTmpFilteredData] = useState();
  // 現在のページ番号
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [totalCount, setTotalCount] = useState(0);
  const [ascSortFlg, setAscSortFlg] = useState('ASC');
  const [sortNm, setSortNm] = useState('id');
  const [filterDatas, setFilterDatas] = useState({
    keyword: '',
    isUsedFlg: 1,
  });
  const [tblLoading, setTblLoading] = useState(false);
  const [isFilterFlg, setIsFilterFlg] = useState(false);

  // 2025-05-20 ブラウザのテキストの拡大率が100％の場合、ページを変える部分(1番下)が、
  // ブラウザを最大化していないと表示されない。90％以下であれば最大化していなくても表示される。
  // 縮小しても表示されるようにしてもらえたらありがたいです。
  const [tableScrollY, setTableScrollY] = useState(500);

  const [mailAddress, setMailAddress] = useState('');
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [displayUpContent, setDisplayUpContent] = useState(false);

  const updateTableHeight = () => {
    const searchHeight = 55;
    const footerHeight = 150;
    const availableHeight = window.innerHeight - searchHeight - footerHeight - 48; // 48 is margin
    setTableScrollY(availableHeight);
  };

  useEffect(() => {
    getUserMasterList(0);
    updateTableHeight();
    window.addEventListener('resize', updateTableHeight);
    return () => window.removeEventListener('resize', updateTableHeight);
  }, [props.userMasterListClick]);

  // フラグ又はキーワードに変更された場合、
  useEffect(() => {
    if (isFilterFlg) {
      // 絞り込み、
      getDataByFilter();
    }
  }, [keyword, dataState, tmpFilteredData]);

  // 会社情報をDBから取得する
  async function getUserMasterList(pageBefore) {
    let userMasterData = [];
    try {
      setCurrentPage(pageBefore);
      let propSortFlg = ascSortFlg;
      let propSortNm = sortNm;
      let propFilterDatas = filterDatas;
      let userMasters = await getUserMasterInfo(pageSize, pageBefore, propSortFlg, propSortNm, propFilterDatas);
      if (userMasters) {
        userMasterData = userMasters ? userMasters?.data : [];
        setTotalCount(userMasters?.totalCount);
      }
      setUserMasterData(userMasterData);
      setFilteredData(userMasterData);
    } catch (e) {
      error(e.message);
      return;
    }
  }

  // ユーザマスタ情報をDBに削除する
  async function deleteUserMasterData(userId, companyMasterId) {
    let ret = false;
    try {
      const Url = process.env.REACT_APP_BACKEND_API_URL + 'UserMaster';
      const otherParam = {
        method: 'DELETE',
        headers: {
          Authorization: 'Bearer ' + getAccessToken(),
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-ZF-APP-KEY': process.env.REACT_APP_API_KEY_AUTH,
        },
        body: JSON.stringify({
          userId: userId,
          companyMasterId: companyMasterId,
        }),
      };

      await fetch(Url, otherParam)
        .then((data) => {
          if (data && data.status === StatusCodes.OK) {
            ret = true;
          } else if (data && data.status === StatusCodes.UNAUTHORIZED) {
            error('アクセス権限がありません。');
          } else if (data && data.status === StatusCodes.NOT_FOUND) {
            error(ErrorMessage()['E012']);
          } else {
            error(ErrorMessage()['E013']);
          }
        })
        .catch((e) => {
          authorizeError(ErrorMessage().E010);
        });
    } catch (e) {
      error(e.message);
    }
    return ret;
  }

  // ユーザマスタ情報をDBに更新する
  async function updateUserMasterData(userId, companyMasterId, mailAddress, userName, userRole) {
    let userMasterInfo = {
      data: [],
      totalCount: 0,
    };
    try {
      const Url = process.env.REACT_APP_BACKEND_API_URL + 'UserMaster';
      const otherParam = {
        method: 'PUT',
        headers: {
          Authorization: 'Bearer ' + getAccessToken(),
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-ZF-APP-KEY': process.env.REACT_APP_API_KEY_AUTH,
        },
        body: JSON.stringify({
          userId: userId,
          companyMasterId: companyMasterId,
          userName: userName,
          userLoginId: mailAddress,
          userRole: Number(userRole),
        }),
      };

      await fetch(Url, otherParam)
        .then(async (response) => {
          return await response?.json();
        })
        .then((data) => {
          if (data && data?.statusCode === StatusCodes.OK) {
            userMasterInfo.data = data?.data;
            userMasterInfo.totalCount = data?.totalRecords;
          } else if (
            data &&
            (data?.statusCode === StatusCodes.UNAUTHORIZED || data?.statusCode === StatusCodes.CONFLICT)
          ) {
            authorizeError(ErrorMessage()[data?.messageCode]);
            return;
          } else if (data && data?.statusCode !== StatusCodes.OK) {
            if (data?.messageCode) {
              error(ErrorMessage()[data?.messageCode]);
            } else {
              let errors = '';
              Object.entries(data?.errors)?.map(([key, data]) => {
                errors += data;
              });
              error('データ更新に失敗しました。' + errors);
            }
            return;
          }
        })
        .catch((e) => {
          authorizeError(ErrorMessage().E010);
          return;
        });
    } catch (e) {
      error(e.message);
    }
    return userMasterInfo;
  }

  const handleTableChange = async (pagination, filters, sorter) => {
    setTblLoading(true); // Waitingダイアログを表示にする
    let pageBefore = pagination?.current - 1;
    let ascSortFlg = 'ASC';
    // if (sorter?.order === undefined) {
    //   ascSortFlg = '';
    // } else if (sorter?.order === 'ascend') {
    //   ascSortFlg = 'ASC';
    // } else if (sorter?.order === 'descend') {
    //   ascSortFlg = 'DESC';
    // }
    // setAscSortFlg(ascSortFlg);
    let userMasterInfos = await getUserMasterInfo(
      pagination?.pageSize,
      pageBefore,
      ascSortFlg,
      sorter?.columnKey,
      filterDatas
    );
    if (userMasterInfos) {
      let userMasterData = userMasterInfos ? userMasterInfos?.data : [];
      setFilteredData(userMasterData);
    }
    setTblLoading(false); // Waitingダイアログを表示にする
  };

  // 削除確認ポップアップのOKボタンを押下
  const deleteOk = async (e) => {
    setIsDelete(false);
    let deletedData = await deleteUserMasterData(selectedRowId, selectedCompanyMasterId);
    if (deletedData) {
      let filterdData = [...filteredData];
      filterdData = filterdData?.filter((item) => item.userId !== selectedRowId);
      setFilteredData(filterdData);

      let currentData = [...userMasterData];
      currentData = currentData?.filter((item) => item.userId !== selectedRowId);

      setUserMasterData(currentData);
      setTmpFilteredData(currentData);
      let deleteAfterTotalCnt = totalCount < 1 ? 0 : totalCount - 1;
      setTotalCount(deleteAfterTotalCnt);
    }
  };

  // 削除確認ポップアップのCandelボタンを押下
  const deleteCancel = (e) => {
    setIsDelete(false);
  };

  // キーワードで検索した場合、
  const searchByKeyword = (e) => {
    keyword === e.target.value ? getDataByFilter() : setKeyWord(e.target.value);
    setIsFilterFlg(true);
  };

  // 絞り込み
  const getDataByFilter = async () => {
    if (userMasterData?.length <= 0) {
      return;
    }

    let searchBarText = keyword;
    let currentData = [...userMasterData];
    let currentFilterData = currentData;

    let filterData = {
      keyword: searchBarText,
      isUsedFlg: dataState,
    };
    setFilterDatas(filterData);
    let userMasterInfos = await getUserMasterInfo(pageSize, 0, ascSortFlg, sortNm, filterData);
    if (userMasterInfos) {
      currentFilterData = userMasterInfos ? userMasterInfos?.data : [];
      setTotalCount(userMasterInfos?.totalCount);
    }
    setFilteredData(currentFilterData);
    setCurrentPage(1);
    setIsFilterFlg(false);
  };

  // 確認ポップアップのOKボタン押下
  const confirmOk = async (e) => {
    setUpdateConfirm(false);
    setDisplayUpContent(false);
    let upData = await updateUserMasterData(selectedRowId, selectedCompanyMasterId, mailAddress, userName, userRole);
    if (upData && upData?.data?.length > 0) {
      let upDataObj = upData?.data[0];
      let filterdData = [...filteredData];

      filterdData.map((item) => {
        if (item.userId == selectedRowId && item.companyMasterId == selectedCompanyMasterId) {
          item.userLoginId = upDataObj.userLoginId;
          item.userName = upDataObj.userName;
          item.loginRole = upDataObj.loginRole;
        }
      });
      setFilteredData(filterdData);

      let currentData = [...userMasterData];
      currentData.map((item) => {
        if (item.userId == selectedRowId && item.companyMasterId == selectedCompanyMasterId) {
          item.userLoginId = upDataObj.userLoginId;
          item.userName = upDataObj.userName;
          item.loginRole = upDataObj.loginRole;
        }
      });
      setUserMasterData(currentData);
      setTmpFilteredData(currentData);
    }
  };

  // 確認ポップアップのCancelボタン押下
  const confirmCancel = () => {
    setUpdateConfirm(false);
  };

  // 破壊確認ポップアップのOKボタン押下
  const upConfirmOK = () => {
    setDisplayUpContent(true);
  };

  // 破壊確認ポップアップのCancelボタン押下
  const upConfirmCancel = () => {
    setDisplayUpContent(false);
  };

  const handlePageSizeChange = (current, pagesize) => {
    setPageSize(pagesize);
  };

  const columns = [
    {
      id: 1,
      title: 'No',
      dataIndex: 'no',
      width: 75,
      render: (id, record, index) => {
        let pageBefore = currentPage - 1;
        let startPage = pageBefore < 0 ? 0 : pageSize * pageBefore;
        return startPage + (index + 1); // IQX_WEBEST-240
      },
      key: 'no',
      className: 'cm-a-right',
    },
    {
      id: 2,
      title: '会社名',
      dataIndex: 'companyName',
      render: (_, record, index) => (
        <Typography.Text
          id={'typographyTxt'}
          style={{ fontSize: '13px', paddingLeft: 10 }}
          ellipsis={{ tooltip: [record?.companyName] }}
        >
          {record?.companyName}
        </Typography.Text>
      ),
      width: 350,
      key: 'companyName',
    },
    {
      id: 3,
      title: 'ユーザー名',
      dataIndex: 'userName',
      render: (_, record, index) => (
        <Typography.Text
          id={'typographyTxt'}
          style={{ fontSize: '13px', paddingLeft: 10 }}
          ellipsis={{ tooltip: [record?.userName] }}
        >
          {record?.userName}
        </Typography.Text>
      ),
      width: 320,
      key: 'userName',
    },
    {
      id: 4,
      title: 'ユーザーID',
      dataIndex: 'userLoginId',
      render: (_, record, index) => (
        <Typography.Text
          id={'typographyTxt'}
          style={{ fontSize: '13px', paddingLeft: 10 }}
          ellipsis={{ tooltip: [record?.userLoginId] }}
        >
          {record?.userLoginId}
        </Typography.Text>
      ),
      width: 350,
      key: 'userLoginId',
    },
    {
      id: 5,
      title: '権限',
      dataIndex: 'loginRole',
      render: (_, record, index) => (
        <Typography.Text id={'typographyTxt'} style={{ fontSize: '13px', paddingLeft: 10 }}>
          {UserRoleIntToString[record?.loginRole]}
        </Typography.Text>
      ),
      width: 150,
      key: 'loginRole',
    },
    {
      id: 6,
      title: '操作',
      width: 250,
      render: (text, record) => (
        <>
          <div style={{ textAlign: 'center', fontSize: 13 }}>
            <Space size={'large'}>
              <a
                onClick={() => {
                  setDisplayUpContent(true);
                  setMailAddress(record.userLoginId);
                  setUserName(record.userName);
                  setUserRole(record.loginRole.toString());
                  setSelectedRowId(record.userId);
                  setSelectedCompanyMasterId(record.companyMasterId);
                }}
              >
                <Image preview={false} width={28} src={editIcon}></Image>
              </a>

              <a
                onClick={() => {
                  setIsDelete(true);
                  setSelectedRowId(record.userId);
                  setSelectedCompanyMasterId(record.companyMasterId);
                }}
              >
                <Image preview={false} width={20} src={deleteIcon}></Image>
              </a>
            </Space>
          </div>
        </>
      ),
    },
  ];

  // テーブル幅Resizable
  const components = {
    header: {
      cell: ResizableTitle,
    },
  };

  //ユーザーマスタ編集タイトル
  const updateModalTitle = (
    <div
      style={{
        width: 520,
        backgroundColor: '#005fab',
        marginLeft: -24,
        height: 36,
        marginTop: -20,
        borderTopLeftRadius: 7,
        borderTopRightRadius: 7,
        fontSize: 14,
      }}
    >
      <p style={{ paddingTop: 8, paddingLeft: 18, color: 'white' }}>ユーザーマスタの編集</p>
    </div>
  );

  //ユーザーマスタ編集
  const updateUserSetting = (
    <>
      <div className="user-master-edit-div">
        {/** ユーザーID */}
        <Row className="mt-2">
          <label>ユーザーID</label>
        </Row>
        <Row className="mt-1">
          <Col span={24}>
            <Input
              name="mailAddress"
              value={mailAddress}
              onChange={(e) => {
                if (ALLOWED_REGEX.test(e.target.value)) {
                  setMailAddress(e.target.value);
                }
              }}
              style={{ padding: 5 }}
              className="input-editable"
            />
          </Col>
        </Row>
        {/** 氏名 */}
        <Row className="mt-2">
          <label>氏名</label>
        </Row>
        <Row className="mt-1">
          <Col span={12}>
            <Input
              name="userName"
              value={userName}
              onChange={(e) => {
                setUserName(e.target.value);
              }}
              style={{ padding: 5 }}
              className="input-editable"
            />
          </Col>
        </Row>
        {/** 権限 */}
        <Row className="mt-2">
          <label>権限</label>
        </Row>
        <Row className="mt-1">
          <Col span={24}>
            <Select
              style={{ width: 120, textAlign: 'left' }}
              popupMatchSelectWidth={false}
              defaultValue={2}
              id={'cmpRole'}
              name={'cmpRole'}
              value={userRole}
              onChange={(e) => {
                setUserRole(e);
              }}
            >
              {CompanyRole?.map((item, index) => (
                <Select.Option key={index + 'benlen'} id={'benlen' + index} value={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>
        {/** 登録ボタン */}
        <Row style={{ marginTop: '3rem' }}>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Button
              className="mainButton"
              onClick={() => {
                setUpdateConfirm(true);
              }}
            >
              更新
            </Button>
          </Col>
        </Row>
      </div>
    </>
  );

  return (
    <>
      {/* 一覧とプロパティ表示 */}
      <div
        style={{
          height: '80vh',
          marginLeft: '1.5rem',
          marginRight: '1.5rem',
        }}
      >
        {/* 検索アリア */}
        <Row style={{ marginTop: 20, marginLeft: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'left', alignItems: 'center', width: '55%' }}>
            <a
              onClick={async () => {
                let pageBefore = 0;
                getUserMasterList(pageBefore);
              }}
            >
              <Image preview={false} width={25} src={reload}></Image>
            </a>
          </div>
          <div style={{ display: 'flex', justifyContent: 'right', alignItems: 'center', width: '39%' }}>
            <Input
              className="input-editable search-select-input"
              style={{ width: 500 }}
              id="keyword"
              placeholder="検索"
              allowClear
              onPressEnter={searchByKeyword}
              onBlur={searchByKeyword}
              onChange={searchByKeyword}
              value={keyword}
              prefix={<SearchOutlined />}
            />
          </div>
        </Row>
        {/* 一覧アリア */}
        <Row style={{ marginTop: 20 }}>
          <Table
            showSorterTooltip={false}
            columns={columns}
            components={components}
            dataSource={filteredData}
            // scroll={{ y: 590, x: '10vw' }}
            scroll={{ y: tableScrollY, x: 'max-content' }}
            rowClassName="data-row"
            pagination={{
              position: ['bottomLeft'],
              total: totalCount,
              showTotal: (total, range) => `全${total}件中${range[0]}件〜${range[1]}件を表示`,
              // defaultPageSize: defaultPageSize,
              defaultCurrent: 1,
              current: currentPage,
              pageSize: pageSize,
              pageSizeOptions: pageSizes,
              showSizeChanger: true,
              onShowSizeChange: handlePageSizeChange,
              onChange: (page) => {
                setCurrentPage(page);
              },
            }}
            onChange={handleTableChange}
            loading={tblLoading}
          />
        </Row>
      </div>

      {/* 削除 */}
      {isDelete
        ? commonResponsiveConfirmModal(
            isDelete,
            confirmModalTitle,
            commonDeleteFooter(deleteOk, deleteCancel),
            null,
            400,
            deleteOk,
            deleteCancel,
            confirmDeleteContent,
            null,
            null,
            false
          )
        : ''}

      {/* 更新コンテンツ */}
      {displayUpContent
        ? commonResponsiveModal(
            displayUpContent,
            updateModalTitle,
            null,
            null,
            520,
            upConfirmOK,
            upConfirmCancel,
            updateUserSetting,
            null,
            null,
            true
          )
        : ''}

      {/* 更新確認 */}
      {updateConfirm
        ? commonResponsiveConfirmModal(
            updateConfirm,
            confirmModalTitle,
            commonFooter(confirmOk, confirmCancel),
            null,
            400,
            confirmOk,
            confirmCancel,
            confirmUpdateContent,
            null,
            null,
            true
          )
        : ''}
    </>
  );
});

export default UserMasterList;
